import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Table, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { PUBLIC_API_BASE_URL } from '../../lib/config';

const BASE_URL = PUBLIC_API_BASE_URL;

const QuoteDetail = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [instructing, setInstructing] = useState(false);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  // Sale-Purchase: ready-made separated Purchase/Sale fee table HTML (from backend, same as email)
  const [salePurchaseFeeHtml, setSalePurchaseFeeHtml] = useState(null);

  useEffect(() => {
    fetchQuoteDetails();
  }, [lead_id]);

  const fetchQuoteDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');

      // Fetch quote details and fee calculation in parallel
      const [quoteResponse, calculationResponse] = await Promise.all([
        axios.get(`${BASE_URL}/customer-portal/lead/${lead_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        // 🎯 NEW: Use fetch_lead_calculations API (same as admin & email)
        axios.get(`${BASE_URL}/customer-portal/lead/${lead_id}/calculation`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (quoteResponse.data.status === 200) {
        const leadData = quoteResponse.data.data;
        console.log('📊 Lead Data:', leadData);
        setQuote(leadData);
      }

      if (calculationResponse.data.status === 200) {
        const calculationData = calculationResponse.data.data;
        console.log('✅ Calculation Data (from fetch_lead_calculations):', calculationData);
        console.log('🔍 Disbursements from API:', calculationData.disbursements);
        console.log('🔍 Stamp Duty from API:', calculationData.disbursements?.stamp_duty_land_tax);

        // 🎯 Sale-Purchase: use the backend-generated separated Purchase/Sale table (same as email)
        setSalePurchaseFeeHtml(calculationData.sale_purchase_fee_html || null);

        // Transform to frontend format (pass lead data to detect product type)
        const transformedData = transformCalculationData(calculationData, quoteResponse.data.data);
        console.log('🔍 Transformed Data:', transformedData);
        console.log('🔍 Stamp Duty after transform:', transformedData.distributionCosts?.stampDuty);
        setFeeBreakdown(transformedData);
      }
    } catch (err) {
      console.error('Error fetching quote details:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/customer-portal/login');
      } else {
        toast.error('Failed to load quote details');
      }
    } finally {
      setLoading(false);
    }
  };

  // Transform fetch_lead_calculations format to QuoteDetail component format
  const transformCalculationData = (data, leadData = null) => {
    // 🎯 Detect Will product (check product title AND service type)
    let isWillProduct = false;

    // Check product_id title/name
    if (leadData?.product_id) {
      const productTitle = (leadData.product_id.title || leadData.product_id.name || '').toLowerCase();
      isWillProduct = productTitle.includes('will');
      console.log('🔍 Product detection - title:', productTitle, 'isWillProduct:', isWillProduct);
    }

    // Also check service_type (for "Single", "Mirror", "LSS-Will", etc.)
    const serviceType = (data.service_type || leadData?.service_type || '').toLowerCase();
    if (serviceType.includes('will') || serviceType === 'single' || serviceType === 'mirror' ||
        serviceType.includes('lss') || serviceType === 'lpa' || serviceType.includes('power of attorney') ||
        serviceType.includes('estate')) {
      isWillProduct = true;
      console.log('🔍 Service type detection - serviceType:', serviceType, 'isWillProduct:', isWillProduct);
    }
    // Helper to create fee/disbursement object
    const createFeeObject = (amount, vatRate = 0.20) => {
      const net = Number(amount || 0);
      const vat = net * vatRate;
      const gross = net + vat;
      return { net, vat, gross };
    };

    // 🎯 CHECK FOR PURCHASE-SALE BREAKDOWNS
    const isPurchaseSale = (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'purchasesale' ||
                           (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'salepurchase';

    if (isPurchaseSale && data.purchase_breakdown && data.sale_breakdown) {
      console.log('🏠🏘️ Purchase-Sale detected! Using separate breakdowns');

      // Transform Purchase breakdown
      const purchaseFees = data.purchase_breakdown.fees || {};
      const purchaseDisbursements = data.purchase_breakdown.disbursements || {};
      const purchaseTotals = data.purchase_breakdown.totals || {};

      // Transform Sale breakdown
      const saleFees = data.sale_breakdown.fees || {};
      const saleDisbursements = data.sale_breakdown.disbursements || {};
      const saleTotals = data.sale_breakdown.totals || {};

      return {
        isPurchaseSale: true,
        purchase: {
          legalCosts: {
            legalFee: createFeeObject(purchaseFees.legalFee?.amount || purchaseFees.legal_fee?.amount || 0),
            hmLandRegistryFee: {
              net: Number(purchaseFees.hm_land_registry_fee?.amount || purchaseFees.hmLandRegistryFee?.amount || 0),
              vat: 0,
              gross: Number(purchaseFees.hm_land_registry_fee?.amount || purchaseFees.hmLandRegistryFee?.amount || 0)
            },
            ttFee: createFeeObject(purchaseFees.ttFee?.amount || purchaseFees.tt_fee?.amount || purchaseFees.telegraphicTransferFee?.amount || 0),
            identityCheck: {
              net: Number(purchaseFees.identityCheck?.amount || purchaseDisbursements.identityCheck?.amount || 0),
              vat: Number(purchaseFees.identityCheck?.vat || purchaseDisbursements.identityCheck?.vat || 0),
              gross: Number(purchaseFees.identityCheck?.gross || purchaseDisbursements.identityCheck?.gross || 0)
            },
            mortgageAdminFee: createFeeObject(purchaseFees.mortgageAdminFee?.amount || purchaseFees.mortgage_admin_fee?.amount || 0),
            leaseholdFee: createFeeObject(purchaseFees.leaseholdFee?.amount || 0),
            helpToBuyISAFee: createFeeObject(purchaseFees.helpToBuyISAFee?.amount || 0),
            helpToBuyFee: createFeeObject(purchaseFees.helpToBuyFee?.amount || 0),
            rightToBuyFee: createFeeObject(purchaseFees.rightToBuyFee?.amount || 0),
            islamicMortgageFee: createFeeObject(purchaseFees.islamicMortgageFee?.amount || 0),
            giftedDepositFeeFirstGiftor: createFeeObject(purchaseFees.giftedDepositFee?.amount || purchaseFees.giftedDepositFeeFirstGiftor?.amount || 0),
            buyToLetFee: createFeeObject(purchaseFees.buyToLetFee?.amount || 0),
            sharedOwnershipFee: createFeeObject(purchaseFees.sharedOwnershipFee?.amount || 0),
            newBuildFee: createFeeObject(purchaseFees.newBuildFee?.amount || 0),
            auctionRepossessionFee: createFeeObject(purchaseFees.auctionRepossessionFee?.amount || 0),
            arrangingIndemnityPolicy: createFeeObject(purchaseFees.arrangingIndemnityPolicy?.amount || 0),
            total: purchaseTotals.legalCost || { net: 0, vat: 0, gross: 0 }
          },
          distributionCosts: {
            stampDuty: {
              net: Number(purchaseDisbursements.stampDutyLandTax?.amount || purchaseDisbursements.stampDuty?.amount || 0),
              vat: 0,
              gross: Number(purchaseDisbursements.stampDutyLandTax?.amount || purchaseDisbursements.stampDuty?.amount || 0)
            },
            localSearchInsurance: {
              net: Number(purchaseDisbursements.local_search_insurance?.amount || purchaseDisbursements.localSearchInsurance?.amount || 0),
              vat: 0,
              gross: Number(purchaseDisbursements.local_search_insurance?.amount || purchaseDisbursements.localSearchInsurance?.amount || 0)
            },
            officialCopiesOfTitle: createFeeObject(purchaseDisbursements.official_copies_of_title?.amount || purchaseDisbursements.officialCopiesOfTitle?.amount || 0, 0),
            landRegistrySearch: {
              net: Number(purchaseDisbursements.landRegistrySearch?.amount || 0),
              vat: Number(purchaseDisbursements.landRegistrySearch?.vat || 0),
              gross: Number(purchaseDisbursements.landRegistrySearch?.gross || 0)
            },
            landChargesSearch: createFeeObject(purchaseDisbursements.landChargesSearch?.amount || 0),
            searchPack: {
              net: Number(purchaseDisbursements.searchPack?.amount || 0),
              vat: 0,
              gross: Number(purchaseDisbursements.searchPack?.amount || 0)
            },
            total: purchaseTotals.disbursementCost || { net: 0, vat: 0, gross: 0 }
          },
          grandTotal: purchaseTotals.total || { net: 0, vat: 0, gross: 0 }
        },
        sale: {
          legalCosts: {
            legalFee: createFeeObject(saleFees.legalFee?.amount || saleFees.legal_fee?.amount || 0),
            ttFee: createFeeObject(saleFees.ttFee?.amount || saleFees.tt_fee?.amount || saleFees.telegraphicTransferFee?.amount || 0),
            identityCheck: {
              net: Number(saleFees.identityCheck?.amount || saleDisbursements.identityCheck?.amount || 0),
              vat: Number(saleFees.identityCheck?.vat || saleDisbursements.identityCheck?.vat || 0),
              gross: Number(saleFees.identityCheck?.gross || saleDisbursements.identityCheck?.gross || 0)
            },
            mortgageAdminFee: createFeeObject(saleFees.mortgageAdminFee?.amount || 0),
            leaseholdFee: createFeeObject(saleFees.leaseholdFee?.amount || 0),
            sharedOwnershipFee: createFeeObject(saleFees.sharedOwnershipFee?.amount || 0),
            auctionRepossessionFee: createFeeObject(saleFees.auctionRepossessionFee?.amount || 0),
            arrangingIndemnityPolicy: createFeeObject(saleFees.arrangingIndemnityPolicy?.amount || 0),
            total: saleTotals.legalCost || { net: 0, vat: 0, gross: 0 }
          },
          distributionCosts: {
            officialCopies: createFeeObject(saleDisbursements.officialCopiesFee?.amount || saleDisbursements.officialCopies?.amount || 0),
            total: saleTotals.disbursementCost || { net: 0, vat: 0, gross: 0 }
          },
          grandTotal: saleTotals.total || { net: 0, vat: 0, gross: 0 }
        },
        grandTotal: data.totals?.total || { net: 0, vat: 0, gross: 0 },
        metadata: {
          serviceType: data.service_type || '',
          branchName: data.branch_name || '',
          isReviewOfLegalPack: false,
          isWillProduct: isWillProduct
        }
      };
    }

    // 🎯 REGULAR SERVICE TYPES (not Purchase-Sale)
    const fees = data.fees || {};
    const disbursements = data.disbursements || {};
    const totals = data.totals || {};

    console.log('🔧 Transform - Input disbursements:', disbursements);

    // Check for stamp duty in multiple possible key names
    const stampDutyAmount = Number(
      disbursements.stamp_duty_land_tax?.amount ||
      disbursements.stampDutyLandTax?.amount ||
      disbursements.stampDuty?.amount ||
      0
    );

    return {
      isPurchaseSale: false,
      legalCosts: {
        legalFee: createFeeObject(fees.legalFee?.amount || fees.legal_fee?.amount || 0),
        ttFee: createFeeObject(fees.ttFee?.amount || fees.tt_fee?.amount || fees.telegraphicTransferFee?.amount || 0),
        identityCheck: {
          net: Number(fees.identityCheck?.amount || disbursements.identityCheck?.amount || 0),
          vat: Number(fees.identityCheck?.vat || disbursements.identityCheck?.vat || 0),
          gross: Number(fees.identityCheck?.gross || disbursements.identityCheck?.gross || 0)
        },
        mortgageAdminFee: createFeeObject(fees.mortgageAdminFee?.amount || fees.mortgage_admin_fee?.amount || 0),
        leaseholdFee: createFeeObject(fees.leaseholdFee?.amount || 0),
        helpToBuyISAFee: createFeeObject(fees.helpToBuyISAFee?.amount || 0),
        helpToBuyFee: createFeeObject(fees.helpToBuyFee?.amount || 0),
        rightToBuyFee: createFeeObject(fees.rightToBuyFee?.amount || 0),
        islamicMortgageFee: createFeeObject(fees.islamicMortgageFee?.amount || 0),
        giftedDepositFeeFirstGiftor: createFeeObject(fees.giftedDepositFee?.amount || fees.giftedDepositFeeFirstGiftor?.amount || 0),
        buyToLetFee: createFeeObject(fees.buyToLetFee?.amount || 0),
        sharedOwnershipFee: createFeeObject(fees.sharedOwnershipFee?.amount || 0),
        newBuildFee: createFeeObject(fees.newBuildFee?.amount || 0),
        auctionRepossessionFee: createFeeObject(fees.auctionRepossessionFee?.amount || 0),
        arrangingIndemnityPolicy: createFeeObject(fees.arrangingIndemnityPolicy?.amount || 0),
        total: totals.legalCost || { net: 0, vat: 0, gross: 0 }
      },
      distributionCosts: {
        stampDuty: { net: stampDutyAmount, vat: 0, gross: stampDutyAmount },
        localSearchInsurance: {
          net: Number(disbursements.local_search_insurance?.amount || disbursements.localSearchInsurance?.amount || 0),
          vat: 0,
          gross: Number(disbursements.local_search_insurance?.amount || disbursements.localSearchInsurance?.amount || 0)
        },
        officialCopiesOfTitle: createFeeObject(disbursements.official_copies_of_title?.amount || disbursements.officialCopiesOfTitle?.amount || 0, 0),
        landRegistrySearch: (() => {
          const net = Number(disbursements.landRegistrySearch?.amount || disbursements.land_registry_search?.amount || 0);
          // ✅ First Registration: NO VAT on Land Registry Search
          const isFirstReg = (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'firstregistration';
          const vat = isFirstReg ? 0 : Number(disbursements.landRegistrySearch?.vat || disbursements.land_registry_search?.vat || 0);
          const gross = isFirstReg ? net : Number(disbursements.landRegistrySearch?.gross || disbursements.land_registry_search?.gross || 0);
          return { net, vat, gross };
        })(),
        landChargesSearch: createFeeObject(disbursements.landChargesSearch?.amount || disbursements.land_charges_search?.amount || 0),
        searchPack: {
          net: Number(disbursements.searchPack?.amount || disbursements.search_pack?.amount || 0),
          vat: 0,
          gross: Number(disbursements.searchPack?.amount || disbursements.search_pack?.amount || 0)
        },
        hmLandRegistryFee: {
          net: Number(disbursements.hmLandRegistryFee?.amount || disbursements.hm_land_registry_fee?.amount || 0),
          vat: 0,
          gross: Number(disbursements.hmLandRegistryFee?.amount || disbursements.hm_land_registry_fee?.amount || 0)
        },
        officialCopies: createFeeObject(disbursements.officialCopiesFee?.amount || disbursements.officialCopies?.amount || disbursements.official_copies?.amount || 0),
        total: (() => {
          // ✅ First Registration: Recalculate disbursement totals with 0 VAT
          const isFirstReg = (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'firstregistration';
          if (isFirstReg) {
            const hmLandRegNet = Number(disbursements.hmLandRegistryFee?.amount || disbursements.hm_land_registry_fee?.amount || 0);
            const landRegSearchNet = Number(disbursements.landRegistrySearch?.amount || disbursements.land_registry_search?.amount || 0);
            const totalNet = hmLandRegNet + landRegSearchNet;
            return { net: totalNet, vat: 0, gross: totalNet };
          }
          return totals.disbursementCost || { net: 0, vat: 0, gross: 0 };
        })()
      },
      grandTotal: (() => {
        // ✅ First Registration: Recalculate grand total with 0 disbursement VAT
        const isFirstReg = (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'firstregistration';
        if (isFirstReg) {
          const legalNet = Number(totals.legalCost?.net || 0);
          const legalVat = Number(totals.legalCost?.vat || 0);
          const hmLandRegNet = Number(disbursements.hmLandRegistryFee?.amount || disbursements.hm_land_registry_fee?.amount || 0);
          const landRegSearchNet = Number(disbursements.landRegistrySearch?.amount || disbursements.land_registry_search?.amount || 0);
          const disbursementNet = hmLandRegNet + landRegSearchNet;
          const totalNet = legalNet + disbursementNet;
          const totalVat = legalVat; // Only legal VAT, no disbursement VAT
          const totalGross = totalNet + totalVat;
          return { net: totalNet, vat: totalVat, gross: totalGross };
        }
        return totals.total || { net: 0, vat: 0, gross: 0 };
      })(),
      metadata: {
        serviceType: data.service_type || '',
        branchName: data.branch_name || '',
        // Normalize service type for Review of Legal Pack (handle spaces, underscores, and hyphens)
        isReviewOfLegalPack: (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'reviewoflegalpack',
        // Check if First Registration
        isFirstRegistration: (data.service_type || '').toLowerCase().replace(/[_\s-]/g, '') === 'firstregistration',
        // Check if Will product
        isWillProduct: isWillProduct
      }
    };
  };

  const handleInstruct = async () => {
    const result = await Swal.fire({
      title: 'Instruct This Quote?',
      html: '<p>Are you sure you want to instruct this quote?</p><p>This will confirm your order and notify us to proceed.</p>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Instruct',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    setInstructing(true);
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.post(
        `${BASE_URL}/customer-portal/lead/${lead_id}/confirm`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 200) {
        toast.success('Quote instructed successfully! We will proceed with your order.');
        fetchQuoteDetails(); // Refresh to show updated status
      }
    } catch (err) {
      console.error('Error instructing quote:', err);
      toast.error(err.response?.data?.message || 'Failed to instruct quote');
    } finally {
      setInstructing(false);
    }
  };

  const formatCurrency = (amount) => {
    return `£${Number(amount || 0).toFixed(2)}`;
  };

  const formatServiceType = (serviceType) => {
    if (!serviceType) return 'N/A';
    return serviceType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: '#764ba2' }} />
        <p className="mt-3 text-muted">Loading quote details...</p>
      </div>
    );
  }

  if (!quote || !feeBreakdown) {
    return (
      <div className="text-center py-5">
        <h5>Quote not found</h5>
        <Button onClick={() => navigate('/customer-portal/my-quotes')} className="red-bg border-0 mt-3">
          Back to My Quotes
        </Button>
      </div>
    );
  }

  // Use backend-calculated fees
  const isPurchaseSale = feeBreakdown.isPurchaseSale;

  // For Purchase-Sale: merge both Purchase and Sale data
  let legalCosts, distributionCosts;

  if (isPurchaseSale) {
    const purchaseLegal = feeBreakdown.purchase.legalCosts;
    const saleLegal = feeBreakdown.sale.legalCosts;
    const purchaseDisb = feeBreakdown.purchase.distributionCosts;
    const saleDisb = feeBreakdown.sale.distributionCosts;

    // Merge legal costs - include both Purchase and Sale fees
    legalCosts = {
      // Purchase fees
      legalFee: purchaseLegal.legalFee,
      ttFee: purchaseLegal.ttFee,
      identityCheck: purchaseLegal.identityCheck,
      mortgageAdminFee: purchaseLegal.mortgageAdminFee,
      leaseholdFee: purchaseLegal.leaseholdFee,
      helpToBuyISAFee: purchaseLegal.helpToBuyISAFee,
      helpToBuyFee: purchaseLegal.helpToBuyFee,
      rightToBuyFee: purchaseLegal.rightToBuyFee,
      islamicMortgageFee: purchaseLegal.islamicMortgageFee,
      giftedDepositFeeFirstGiftor: purchaseLegal.giftedDepositFeeFirstGiftor,
      buyToLetFee: purchaseLegal.buyToLetFee,
      sharedOwnershipFee: purchaseLegal.sharedOwnershipFee,
      newBuildFee: purchaseLegal.newBuildFee,
      auctionRepossessionFee: purchaseLegal.auctionRepossessionFee,
      arrangingIndemnityPolicy: purchaseLegal.arrangingIndemnityPolicy,
      // Sale fees (prefixed)
      saleLegalFee: saleLegal.legalFee,
      saleTtFee: saleLegal.ttFee,
      saleIdentityCheck: saleLegal.identityCheck,
      saleMortgageAdminFee: saleLegal.mortgageAdminFee,
      saleLeaseholdFee: saleLegal.leaseholdFee,
      saleSharedOwnershipFee: saleLegal.sharedOwnershipFee,
      saleAuctionRepossessionFee: saleLegal.auctionRepossessionFee,
      saleArrangingIndemnityPolicy: saleLegal.arrangingIndemnityPolicy,
      total: purchaseLegal.total
    };

    // Merge disbursements - include both Purchase and Sale
    distributionCosts = {
      // Purchase disbursements
      stampDuty: purchaseDisb.stampDuty,
      hmLandRegistryFee: purchaseDisb.hmLandRegistryFee,
      landRegistrySearch: purchaseDisb.landRegistrySearch,
      landChargesSearch: purchaseDisb.landChargesSearch,
      searchPack: purchaseDisb.searchPack,
      // Sale disbursements
      officialCopies: saleDisb.officialCopies,
      total: purchaseDisb.total
    };
  } else {
    legalCosts = feeBreakdown.legalCosts;
    distributionCosts = feeBreakdown.distributionCosts;
  }

  const grandTotalData = feeBreakdown.grandTotal;
  const metadata = feeBreakdown.metadata;

  // Purchase-Sale specific data (for reference)
  const purchaseData = isPurchaseSale ? feeBreakdown.purchase : null;
  const saleData = isPurchaseSale ? feeBreakdown.sale : null;

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <Button
          variant="link"
          onClick={() => navigate('/customer-portal/my-quotes')}
          className="text-decoration-none mb-2 p-0"
          style={{ color: '#764ba2' }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Back to My Quotes
        </Button>
        <h2 className="mb-2">
          My Quote <span className="text-muted fw-normal">{formatServiceType(quote.service_type)}, Ref {quote.lead_id}</span>
        </h2>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-4">
          {/* About Your Purchase/Sale Section */}
          <div className="mb-4 pb-4" style={{ borderBottom: '2px solid #f0f0f0' }}>
            <h5 className="mb-3" style={{ color: '#333', fontWeight: '600' }}>About Your {formatServiceType(quote.service_type)}</h5>

            {/* For Purchase_Sale: Show separate sections */}
            {(() => {
              // Check all possible Sale-Purchase variations
              const serviceType = (quote.service_type || '').toLowerCase().replace(/[_\s-]/g, '');
              const isSalePurchase = serviceType === 'salepurchase' || serviceType === 'purchasesale';
              return isSalePurchase;
            })() ? (
              <>
                {/* Purchase Section */}
                <h6 className="mb-2 mt-3" style={{ color: '#2a5298', fontWeight: '600' }}>
                  <i className="bi bi-house-door me-2"></i>Purchase Details
                </h6>
                <Table bordered className="mb-3" style={{ backgroundColor: '#fff' }}>
                  <tbody>
                    {(() => {
                      // Check multiple possible field names for purchase address
                      const addr = quote.purchase_property_address ||
                                   quote.purchasePropertyDetails?.address ||
                                   quote.property_address;
                      return addr;
                    })() && (
                      <tr>
                        <td style={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Property</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                          {(() => {
                            const addr = quote.purchase_property_address ||
                                       quote.purchasePropertyDetails?.address ||
                                       quote.property_address;
                            return typeof addr === 'string'
                              ? addr
                              : [
                                  addr.address1 || addr.line1,
                                  addr.address2 || addr.line2,
                                  addr.city || addr.town,
                                  addr.county || addr.country,
                                  addr.postcode
                                ]
                                  .filter(part => part && part.trim() !== '')
                                  .join(', ');
                          })()}
                        </td>
                      </tr>
                    )}
                    {(() => {
                      const price = quote.purchase_property_value ||
                                   quote.purchasePropertyDetails?.purchasePrice ||
                                   quote.property_value;
                      return price;
                    })() && (
                      <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Price</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                          {formatCurrency(quote.purchase_property_value ||
                                        quote.purchasePropertyDetails?.purchasePrice ||
                                        quote.property_value)}
                        </td>
                      </tr>
                    )}
                    {quote.ownership_type && (
                      <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Tenure</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{quote.ownership_type}</td>
                      </tr>
                    )}
                    {quote.completion_date && (
                      <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Est. Completion</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                          {new Date(quote.completion_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Purchase Info</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(() => {
                            const info = [];
                            const purchaseInfo = quote.purchase_info || quote.info || {};
                            const conv = quote.conveyancing_details || {};
                            const isTrue = (val) => val === true || val === 'true' || val === 'Yes' || val === 'yes' || val === 1 || val === '1';

                            if (isTrue(purchaseInfo.first_time_buyer) || isTrue(purchaseInfo.firstTimeBuyer) || isTrue(quote.first_time_buyer) || isTrue(conv.first_time_buyer)) info.push('First Time Buyer');
                            if (isTrue(purchaseInfo.help_to_buy_isa) || isTrue(purchaseInfo.helpToBuyISA) || isTrue(quote.help_to_buy_isa) || isTrue(conv.help_to_buy_isa)) info.push('Help to Buy ISA');
                            if (isTrue(purchaseInfo.buying_with_mortgage) || isTrue(purchaseInfo.buyingWithMortgage) || isTrue(quote.buying_with_mortgage) || isTrue(conv.buying_with_mortgage)) info.push('Buying with Mortgage');
                            if (isTrue(purchaseInfo.new_build) || isTrue(purchaseInfo.newBuild) || isTrue(quote.new_build) || isTrue(conv.new_build)) info.push('New Build');

                            if (info.length === 0) return <span style={{ color: '#6c757d' }}>No additional info</span>;
                            return info.map((item, index) => (<Badge key={index} bg="primary" className="me-1 mb-1">{item}</Badge>));
                          })()}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Sale Section */}
                <h6 className="mb-2 mt-3" style={{ color: '#28a745', fontWeight: '600' }}>
                  <i className="bi bi-sign-turn-right me-2"></i>Sale Details
                </h6>
                <Table bordered className="mb-3" style={{ backgroundColor: '#fff' }}>
                  <tbody>
                    {(() => {
                      // Check multiple possible field names for sale address
                      const addr = quote.sale_property_address ||
                                   quote.salePropertyDetails?.address;
                      return addr;
                    })() && (
                      <tr>
                        <td style={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Property</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                          {(() => {
                            const addr = quote.sale_property_address ||
                                       quote.salePropertyDetails?.address;
                            return typeof addr === 'string'
                              ? addr
                              : [
                                  addr.address1 || addr.line1,
                                  addr.address2 || addr.line2,
                                  addr.city || addr.town,
                                  addr.county || addr.country,
                                  addr.postcode
                                ]
                                  .filter(part => part && part.trim() !== '')
                                  .join(', ');
                          })()}
                        </td>
                      </tr>
                    )}
                    {(() => {
                      const price = quote.sale_property_value ||
                                   quote.salePropertyDetails?.salePrice;
                      return price;
                    })() && (
                      <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Price</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                          {formatCurrency(quote.sale_property_value ||
                                        quote.salePropertyDetails?.salePrice)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Sale Info</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(() => {
                            const info = [];
                            const saleInfo = quote.sale_info || quote.info || {};
                            const conv = quote.conveyancing_details || {};
                            const isTrue = (val) => val === true || val === 'true' || val === 'Yes' || val === 'yes' || val === 1 || val === '1';

                            if (isTrue(saleInfo.selling_with_mortgage) || isTrue(saleInfo.sellingWithMortgage) || isTrue(quote.selling_with_mortgage) || isTrue(conv.selling_with_mortgage)) info.push('Selling with Mortgage');
                            if (isTrue(saleInfo.shared_ownership) || isTrue(saleInfo.sharedOwnership) || isTrue(quote.shared_ownership) || isTrue(conv.shared_ownership)) info.push('Shared Ownership');

                            if (info.length === 0) return <span style={{ color: '#6c757d' }}>No additional info</span>;
                            return info.map((item, index) => (<Badge key={index} bg="success" className="me-1 mb-1">{item}</Badge>));
                          })()}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Common Info */}
                <Table bordered className="mb-0" style={{ backgroundColor: '#fff' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Service Type</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                        {formatServiceType(quote.service_type)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Status</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                        {quote.customer_confirmed ? (
                          <Badge bg="success">Instructed</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Quote Sent</Badge>
                        )}
                      </td>
                    </tr>
                    {quote.customer_ids?.[0] && (
                      <tr>
                        <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Client</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                          {quote.customer_ids[0]?.title || ''} {quote.customer_ids[0]?.forename || ''} {quote.customer_ids[0]?.surname || ''}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Reference</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{quote.lead_id}</td>
                    </tr>
                  </tbody>
                </Table>
              </>
            ) : (
              /* For other services: Single table */
              <Table bordered className="mb-0" style={{ backgroundColor: '#fff' }}>
                <tbody>
                <tr>
                  <td style={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Service Type</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                    {formatServiceType(quote.service_type)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Status</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                    {quote.customer_confirmed ? (
                      <Badge bg="success">Instructed</Badge>
                    ) : (
                      <Badge bg="warning" text="dark">Quote Sent</Badge>
                    )}
                  </td>
                </tr>
                {quote.customer_ids?.[0] && (
                  <tr>
                    <td style={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Client</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                      {quote.customer_ids[0]?.title || ''} {quote.customer_ids[0]?.forename || ''} {quote.customer_ids[0]?.surname || ''}
                    </td>
                  </tr>
                )}
                {quote.address && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Property</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                      {typeof quote.address === 'string'
                        ? quote.address
                        : [
                            quote.address.address1 || quote.address.line1,
                            quote.address.address2 || quote.address.line2,
                            quote.address.city || quote.address.town,
                            quote.address.county || quote.address.country,
                            quote.address.postcode
                          ]
                            .filter(part => part && part.trim() !== '')
                            .join(', ')
                      }
                    </td>
                  </tr>
                )}
                {(quote.property_value || quote.net_value) && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Price</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{formatCurrency(quote.property_value || quote.net_value)}</td>
                  </tr>
                )}
                {quote.ownership_type && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Tenure</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{quote.ownership_type}</td>
                  </tr>
                )}
                {quote.service_type !== 'Sale' && quote.completion_date && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Est. Completion Date</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                      {new Date(quote.completion_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                  </tr>
                )}
                {quote.service_type === 'Sale' && (quote.info || quote.conveyancing_details) && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Sale Info</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(() => {
                          const info = [];
                          const saleInfo = quote.info || {};
                          const conv = quote.conveyancing_details || {};

                          // Sale-specific info fields
                          if (saleInfo.sellingWithMortgage === true || quote.selling_with_mortgage === true || conv.selling_with_mortgage === true) info.push('Selling with Mortgage');
                          if (saleInfo.unregisteredProperty === true || quote.unregistered_property === true || conv.unregistered_property === true) info.push('Unregistered Property');
                          if (saleInfo.sharedOwnership === true || quote.shared_ownership === true || conv.shared_ownership === true) info.push('Shared Ownership');
                          if (saleInfo.auctionRepossession === true || quote.auction_repossession === true || conv.auction_repossession === true) info.push('Auction / Repossession');
                          if (saleInfo.arrangingIndemnityPolicy === true || quote.arranging_indemnity_policy === true || conv.arranging_indemnity_policy === true) info.push('Arranging an Indemnity Policy');

                          if (info.length === 0) return <span style={{ color: '#6c757d' }}>No additional sale info</span>;

                          return info.map((item, index) => (
                            <Badge key={index} bg="success" className="me-1 mb-1">{item}</Badge>
                          ));
                        })()}
                      </div>
                    </td>
                  </tr>
                )}
                {quote.service_type !== 'Sale' && quote.service_type !== 'Transfer_of_Equity' && quote.service_type !== 'Transfer of Equity' && (quote.info || quote.conveyancing_details) && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Purchase Info</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(() => {
                          const info = [];
                          const purchaseInfo = quote.info || {};
                          const conv = quote.conveyancing_details || {};

                          // Purchase-specific info fields
                          if (purchaseInfo.firstTimeBuyer === true || quote.first_time_buyer === true || conv.first_time_buyer === true) info.push('First Time Buyer');
                          if (purchaseInfo.helpToBuyISA === true || quote.help_to_buy_isa === true || conv.help_to_buy_isa === true) info.push('Help to Buy ISA');
                          if (purchaseInfo.helpToBuy === true || quote.help_to_buy === true || conv.help_to_buy === true) info.push('Help to Buy');
                          if (purchaseInfo.helpToBuyEquityLoan === true || quote.help_to_buy_equity_loan === true || conv.help_to_buy_equity_loan === true) info.push('Help to Buy Equity Loan');
                          if (purchaseInfo.rightToBuy === true || quote.right_to_buy === true || conv.right_to_buy === true) info.push('Right to Buy');
                          if (purchaseInfo.sharedOwnership === true || quote.shared_ownership === true || conv.shared_ownership === true) info.push('Shared Ownership');
                          if (purchaseInfo.buyingWithMortgage === true || quote.buying_with_mortgage === true || conv.buying_with_mortgage === true) info.push('Buying with Mortgage');
                          if (purchaseInfo.newBuild === true || quote.new_build === true || conv.new_build === true) info.push('New Build');
                          if (purchaseInfo.islamicMortgage === true || quote.islamic_mortgage === true || conv.islamic_mortgage === true) info.push('Islamic Mortgage');
                          if (purchaseInfo.auctionRepossession === true || quote.auction_repossession === true || conv.auction_repossession === true) info.push('Auction / Repossession');
                          if (purchaseInfo.giftedDeposit === true || quote.gifted_deposit === true || conv.gifted_deposit === true) info.push('Gifted Deposit');
                          if (purchaseInfo.multipleProperties === true || quote.multiple_properties === true || conv.multiple_properties === true) info.push('Multiple Properties');
                          if (purchaseInfo.buyToLet === true || quote.buy_to_let === true || conv.buy_to_let === true) info.push('Buy to Let');
                          if (purchaseInfo.arrangingIndemnityPolicy === true || quote.arranging_indemnity_policy === true || conv.arranging_indemnity_policy === true) info.push('Arranging an Indemnity Policy');
                          if (purchaseInfo.unregisteredProperty === true || quote.unregistered_property === true || conv.unregistered_property === true) info.push('Unregistered Property');
                          if (purchaseInfo.staircasing === true || quote.staircasing === true || conv.staircasing === true) info.push('Staircasing');
                          if (purchaseInfo.nonResidentialMixed === true || quote.non_residential_mixed === true || conv.non_residential_mixed === true) info.push('Non-Residential / Mixed Use');

                          const isNonUK = purchaseInfo.isNonUKResident || quote.is_non_uk_resident || conv.is_non_uk_resident;
                          if (isNonUK === 'Yes' || isNonUK === true) info.push('Non-UK Resident');

                          if (info.length === 0) return <span style={{ color: '#6c757d' }}>No additional purchase info</span>;

                          return info.map((item, index) => (
                            <Badge key={index} bg="primary" className="me-1 mb-1">{item}</Badge>
                          ));
                        })()}
                      </div>
                    </td>
                  </tr>
                )}
                {(quote.service_type === 'Transfer_of_Equity' || quote.service_type === 'Transfer of Equity') && (quote.info || quote.conveyancing_details) && (
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Transfer Info</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(() => {
                          const info = [];
                          const transferInfo = quote.info || {};
                          const conv = quote.conveyancing_details || {};
                          const isTrue = (val) => val === true || val === 'true' || val === 'Yes' || val === 'yes' || val === 1 || val === '1';

                          // Transfer-specific info fields
                          if (isTrue(transferInfo.buying_out_partner) || isTrue(transferInfo.buyingOutPartner) || isTrue(quote.buying_out_partner) || isTrue(conv.buying_out_partner)) info.push('Buying Out Partner');
                          if (isTrue(transferInfo.adding_name_to_title) || isTrue(transferInfo.addingNameToTitle) || isTrue(quote.adding_name_to_title) || isTrue(conv.adding_name_to_title)) info.push('Adding Name to Title');
                          if (isTrue(transferInfo.removing_name_from_title) || isTrue(transferInfo.removingNameFromTitle) || isTrue(quote.removing_name_from_title) || isTrue(conv.removing_name_from_title)) info.push('Removing Name from Title');
                          if (isTrue(transferInfo.property_is_mortgaged) || isTrue(transferInfo.propertyIsMortgaged) || isTrue(quote.property_is_mortgaged) || isTrue(conv.property_is_mortgaged)) info.push('Property is Mortgaged');
                          if (isTrue(transferInfo.requires_local_search_insurance) || isTrue(transferInfo.requiresLocalSearchInsurance) || isTrue(quote.requires_local_search_insurance) || isTrue(conv.requires_local_search_insurance)) info.push('Requires Local Search Insurance');
                          if (isTrue(transferInfo.first_time_buyer) || isTrue(transferInfo.firstTimeBuyer) || isTrue(quote.first_time_buyer) || isTrue(conv.first_time_buyer)) info.push('First Time Buyer');

                          if (info.length === 0) return <span style={{ color: '#6c757d' }}>No additional transfer info</span>;

                          return info.map((item, index) => (
                            <Badge key={index} bg="info" className="me-1 mb-1">{item}</Badge>
                          ));
                        })()}
                      </div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Reference</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{quote.lead_id}</td>
                </tr>
              </tbody>
            </Table>
            )}
          </div>

          {/* Quote Price Section */}
          <div className="d-flex justify-content-between align-items-center mb-4 pb-4" style={{ borderBottom: '2px solid #f0f0f0' }}>
            <div>
              <h5 className="text-muted mb-2">Your Quote Price:</h5>
              <h2 className="mb-0" style={{ color: '#764ba2', fontWeight: 'bold' }}>
                {formatCurrency(grandTotalData.gross)}
                <span className="text-muted" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                  {' '}inc. VAT
                </span>
              </h2>
            </div>
            {!quote.customer_confirmed && (
              <Button
                className="red-bg border-0 px-4 py-2"
                onClick={handleInstruct}
                disabled={instructing}
                style={{ fontSize: '16px', fontWeight: 'bold' }}
              >
                {instructing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Instructing...
                  </>
                ) : (
                  'Instruct'
                )}
              </Button>
            )}
            {quote.customer_confirmed && (
              <Badge bg="success" className="px-4 py-3" style={{ fontSize: '16px' }}>
                <i className="bi bi-check-circle me-2"></i>
                Instructed
              </Badge>
            )}
          </div>

          {/* Quote Breakdown Table */}
          {/* Sale-Purchase: render the separated Purchase/Sale table (same as email) */}
          {salePurchaseFeeHtml ? (
            <div dangerouslySetInnerHTML={{ __html: salePurchaseFeeHtml }} />
          ) : metadata.isReviewOfLegalPack ? (
            <Table bordered className="mb-0" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th style={{ width: '50%', border: '1px solid #dee2e6', padding: '10px', fontWeight: '600' }}>Description</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '10px', textAlign: 'right', fontWeight: '600' }}>NET</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '10px', textAlign: 'right', fontWeight: '600' }}>VAT</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '10px', textAlign: 'right', fontWeight: '600' }}>GROSS</th>
                </tr>
              </thead>
              <tbody>
                {/* Legal Cost Section Header */}
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td colSpan="4" style={{ border: '1px solid #dee2e6', padding: '8px', fontWeight: '600' }}>Legal Cost</td>
                </tr>
                {/* Legal Fee with ownership type */}
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>
                    Legal Fee ({quote.ownership_type ? quote.ownership_type.toLowerCase() : 'freehold'})
                  </td>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>{formatCurrency(legalCosts.legalFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>{formatCurrency(legalCosts.legalFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>{formatCurrency(legalCosts.legalFee.gross)}</td>
                </tr>
                {/* Total Legal Cost */}
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>TOTAL LEGAL COST</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>{formatCurrency(grandTotalData.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>{formatCurrency(grandTotalData.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>{formatCurrency(grandTotalData.gross)}</td>
                </tr>
                {/* Grand Total - matches email exactly */}
                <tr style={{ background: '#2a5298', color: '#ffffff', fontWeight: 'bold', fontSize: '16px' }}>
                  <td style={{ color: '#ffffff', padding: '12px' }}>TOTAL (Legal Cost + Disbursement Cost)</td>
                  <td className="text-end" style={{ color: '#ffffff', padding: '12px' }}>{formatCurrency(grandTotalData.net)}</td>
                  <td className="text-end" style={{ color: '#ffffff', padding: '12px' }}>{formatCurrency(grandTotalData.vat)}</td>
                  <td className="text-end" style={{ color: '#ffffff', padding: '12px' }}>{formatCurrency(grandTotalData.gross)}</td>
                </tr>
              </tbody>
            </Table>
          ) : (
            /* Standard detailed table for other service types */
            <Table bordered className="mb-0" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th style={{ width: '50%', border: '1px solid #dee2e6', padding: '6px', fontWeight: '600' }}>{formatServiceType(quote.service_type)}</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right', fontWeight: '600' }}>Net</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right', fontWeight: '600' }}>VAT</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right', fontWeight: '600' }}>Gross</th>
                </tr>
              </thead>
              <tbody>
                {/* Legal Fees Section */}
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td colSpan="4" style={{ border: '1px solid #dee2e6', padding: '6px', fontWeight: '600' }}>Legal Costs</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Legal Fees</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.legalFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.legalFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.legalFee.gross)}</td>
                </tr>
              {legalCosts.leaseholdFee.net > 0 && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Leasehold Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.leaseholdFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.leaseholdFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.leaseholdFee.gross)}</td>
                </tr>
              )}
              {/* Hide TT Fee for Review of Legal Pack and First Registration */}
              {!metadata.isReviewOfLegalPack && !metadata.isFirstRegistration && legalCosts.ttFee.net > 0 && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Telegraphic Transfer (TT) Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.ttFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.ttFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.ttFee.gross)}</td>
                </tr>
              )}
              {/* Identity Check - show for all services except Review of Legal Pack */}
              {!metadata.isReviewOfLegalPack && legalCosts.identityCheck.net > 0 && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Identity Check</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.identityCheck.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.identityCheck.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.identityCheck.gross)}</td>
                </tr>
              )}
              {legalCosts.mortgageAdminFee.net > 0 && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Mortgage Admin Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.mortgageAdminFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.mortgageAdminFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.mortgageAdminFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.helpToBuyISA === true || quote?.conveyancing_details?.help_to_buy_isa === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Help to Buy ISA Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.helpToBuyISAFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.helpToBuyISAFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.helpToBuyISAFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.helpToBuy === true || quote?.conveyancing_details?.help_to_buy === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Help to Buy Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.helpToBuyFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.helpToBuyFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.helpToBuyFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.rightToBuy === true || quote?.conveyancing_details?.right_to_buy === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Right to Buy Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.rightToBuyFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.rightToBuyFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.rightToBuyFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.islamicMortgage === true || quote?.conveyancing_details?.islamic_mortgage === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Islamic Mortgage Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.islamicMortgageFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.islamicMortgageFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.islamicMortgageFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.giftedDeposit > 0 || quote?.conveyancing_details?.gifted_deposit === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Gifted Deposit Fee (First Giftor)</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.giftedDepositFeeFirstGiftor.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.giftedDepositFeeFirstGiftor.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.giftedDepositFeeFirstGiftor.gross)}</td>
                </tr>
              )}
              {(quote?.info?.buyToLet === true || quote?.conveyancing_details?.buy_to_let === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Buy to Let Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.buyToLetFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.buyToLetFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.buyToLetFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.sharedOwnership === true || quote?.conveyancing_details?.shared_ownership === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Shared Ownership Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.sharedOwnershipFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.sharedOwnershipFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.sharedOwnershipFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.newBuild === true || quote?.conveyancing_details?.new_build === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>New Build Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.newBuildFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.newBuildFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.newBuildFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.auctionRepossession === true || quote?.conveyancing_details?.auction_repossession === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Auction / Repossession Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.auctionRepossessionFee.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.auctionRepossessionFee.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.auctionRepossessionFee.gross)}</td>
                </tr>
              )}
              {(quote?.info?.arrangingIndemnityPolicy === true || quote?.conveyancing_details?.arranging_indemnity_policy === true) && (
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Arranging an Indemnity Policy</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.arrangingIndemnityPolicy.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.arrangingIndemnityPolicy.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.arrangingIndemnityPolicy.gross)}</td>
                </tr>
              )}

              {/* Total Legal Cost - Hide for Review of Legal Pack and Will Products */}
              {!metadata.isReviewOfLegalPack && !metadata.isWillProduct && (
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>TOTAL LEGAL COST</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.total.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.total.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts.total.gross)}</td>
                </tr>
              )}

              {/* Hide entire Disbursement section for Review of Legal Pack and Will Products */}
              {!metadata.isReviewOfLegalPack && !metadata.isWillProduct && (
                <>
                  {/* Third Party Expenditure Section */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4" style={{ border: '1px solid #dee2e6', padding: '6px', fontWeight: '600' }}>Disbursement – Third Party Expenditure</td>
                  </tr>
                  {/* For Sale: show Official Copies only. For Purchase and Purchase-Sale: show all disbursements */}
                  {quote.service_type === 'Sale' ? (
                    <tr>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Official Copies</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.officialCopies?.net)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{distributionCosts.officialCopies?.vat > 0 ? formatCurrency(distributionCosts.officialCopies.vat) : '-'}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.officialCopies?.gross)}</td>
                    </tr>
                  ) : (
                    <>
                      {/* Purchase Disbursements */}
                      {distributionCosts.localSearchInsurance?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Local Search Insurance</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.localSearchInsurance.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>-</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.localSearchInsurance.gross)}</td>
                        </tr>
                      )}
                      {distributionCosts.officialCopiesOfTitle?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Official Copies of Title</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.officialCopiesOfTitle.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>-</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.officialCopiesOfTitle.gross)}</td>
                        </tr>
                      )}
                      {distributionCosts.stampDuty?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Stamp Duty Land Tax (SDLT)</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.stampDuty.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>-</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.stampDuty.gross)}</td>
                        </tr>
                      )}
                      {distributionCosts.landRegistrySearch?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Land Registry Search</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.landRegistrySearch.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.landRegistrySearch.vat)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.landRegistrySearch.gross)}</td>
                        </tr>
                      )}
                      {distributionCosts.landChargesSearch?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Land Charges Search</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.landChargesSearch.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.landChargesSearch.vat)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.landChargesSearch.gross)}</td>
                        </tr>
                      )}
                      {distributionCosts.searchPack?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Search Pack - Local Authority, Water & Drainage, Environmental Search</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.searchPack.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>-</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.searchPack.gross)}</td>
                        </tr>
                      )}
                      {distributionCosts.hmLandRegistryFee?.net > 0 && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>HM Land Registry Fee</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.hmLandRegistryFee.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>-</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.hmLandRegistryFee.gross)}</td>
                        </tr>
                      )}
                      {/* For Purchase-Sale: Also show Official Copies from Sale */}
                      {isPurchaseSale && distributionCosts.officialCopies && (
                        <tr>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Official Copies (Sale)</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.officialCopies?.net)}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{distributionCosts.officialCopies?.vat > 0 ? formatCurrency(distributionCosts.officialCopies.vat) : '-'}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.officialCopies?.gross)}</td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* Total Distribution Cost */}
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>TOTAL DISBURSEMENT COST </td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.total.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.total.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts.total.gross)}</td>
                  </tr>
                </>
              )}

              {/* Grand Total */}
              <tr style={{ background: '#2a5298', color: '#ffffff', fontWeight: 'bold', fontSize: '16px' }}>
                <td style={{ color: '#ffffff' }}>
                  {metadata.isWillProduct ? 'TOTAL (Legal Cost)' : 'TOTAL (Legal Cost + Disbursement Cost)'}
                </td>
                <td className="text-end" style={{ color: '#ffffff' }}>{formatCurrency(grandTotalData.net)}</td>
                <td className="text-end" style={{ color: '#ffffff' }}>{formatCurrency(grandTotalData.vat)}</td>
                <td className="text-end" style={{ color: '#ffffff' }}>{formatCurrency(grandTotalData.gross)}</td>
              </tr>
            </tbody>
          </Table>
          )}
        </Card.Body>
      </Card>

      {/* Documents Section */}
      {quote.documents && quote.documents.length > 0 && (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4">
            <h5 className="mb-3">Document(s)</h5>
            <ul className="mb-0" style={{ paddingLeft: '20px' }}>
              {quote.documents.map((doc, index) => (
                <li key={index} className="mb-2">{doc}</li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

    </div>
  );
};

export default QuoteDetail;
